import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Home } from 'lucide-react';
import './StrategicPartnersPage.css';

const StrategicPartnersPage = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const correctPassword = 'Park Street Homes';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === correctPassword) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password. Please try again.');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="sp-auth-page">
        <div className="sp-auth-container">
          <div className="sp-auth-icon">
            <Lock size={48} />
          </div>
          <h1 className="sp-auth-title">Strategic Partners</h1>
          <p className="sp-auth-subtitle">This page is password protected</p>
          <form onSubmit={handleSubmit} className="sp-auth-form">
            <div className="sp-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="sp-auth-input"
                data-testid="partner-password-input"
              />
              <button
                type="button"
                className="sp-toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {error && <p className="sp-auth-error">{error}</p>}
            <button type="submit" className="sp-auth-button" data-testid="partner-submit-btn">
              Access Page
            </button>
          </form>
          <button 
            className="sp-back-home-btn" 
            onClick={() => navigate('/')}
            data-testid="back-home-btn"
          >
            <Home size={18} />
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sp-page">
      <div className="sp-content">
        {/* HEADER */}
        <div className="sp-header">
          <div className="sp-header-psh">
            <span className="sp-header-label">PARK STREET HOMES <span className="gold">·</span> COMMUNITY HOMEBUILDER</span>
          </div>
          <div className="sp-header-sep"></div>
          <div className="sp-header-r6">
            <span className="sp-header-label">THE <span className="gold">R6</span> COLLECTIVE <span className="gold">·</span> WORKFORCE DEVELOPMENT</span>
          </div>
        </div>

        {/* HERO */}
        <div className="sp-hero">
          <div className="sp-hero-psh">
            <div className="sp-psh-wordmark">
              <div className="sp-psh-wm-top">PARK STREET</div>
              <div className="sp-psh-wm-bot">HOMES</div>
            </div>
            <div className="sp-hero-hl">Building <em>homes</em> that<br/>elevate people's lives.</div>
            <div className="sp-hero-sub">Affordable Housing · Community Renewal · Houston &amp; Birmingham</div>
          </div>

          <div className="sp-hero-mid">
            <div className="sp-x-sym">×</div>
            <div className="sp-x-lbl">Partnership</div>
          </div>

          <div className="sp-hero-r6">
            <div className="sp-r6-logo-wrap">
              <div className="sp-r6-wordmark">
                <div className="sp-r6-wm-top">THE R6</div>
                <div className="sp-r6-wm-bot">COLLECTIVE</div>
              </div>
              <div className="sp-r6-grid">
                <div className="sp-r6-sq"></div><div className="sp-r6-sq gold"></div>
                <div className="sp-r6-sq"></div><div className="sp-r6-sq"></div>
                <div className="sp-r6-sq"></div><div className="sp-r6-sq"></div>
              </div>
            </div>
            <div className="sp-hero-hl-r6">Building <em>careers</em><br/>that transform communities.</div>
            <div className="sp-hero-sub">Fortune 1000 Expertise · Workforce Development · Small & Midsized Business</div>
          </div>
        </div>

        {/* GOLD BAND */}
        <div className="sp-gold-band">
          <div className="sp-gb-eyebrow">A Community-First Partnership</div>
          <div className="sp-gb-headline">Where Every Home Built Becomes a Gateway to Opportunity</div>
          <div className="sp-gb-body">Park Street Homes and The R6™ Collective share a singular conviction: communities thrive when people have both a place to call home and the skills to build a prosperous future. Together, we deliver an integrated model — affordable, high-quality housing paired with Fortune 1000-caliber workforce development — creating lasting economic mobility in the very neighborhoods we serve.</div>
        </div>

        {/* DUAL PANELS */}
        <div className="sp-dual">
          {/* PSH Panel */}
          <div className="sp-panel sp-panel-psh">
            <div className="sp-sec-label">What Park Street Homes Brings</div>
            <div className="sp-panel-heading">Renewing Communities,<br/>One Home at a Time</div>
            <div className="sp-panel-body">Park Street Homes is a community-first homebuilder dedicated to crafting high-quality, affordable residences that don't just house families — they anchor neighborhoods. Operating in Houston and Birmingham, we invest deeply in the places we build, prioritizing sustainable practices, thoughtful design, and the long-term vitality of every community we enter.</div>
            <div className="sp-pillars">
              <div className="sp-pillar sp-pillar-psh">
                <div className="sp-p-num">01</div>
                <div className="sp-p-title">Affordable Housing</div>
                <div className="sp-p-desc">Quality homes built to be accessible for working families in underserved communities.</div>
              </div>
              <div className="sp-pillar sp-pillar-psh">
                <div className="sp-p-num">02</div>
                <div className="sp-p-title">Community Renewal</div>
                <div className="sp-p-desc">Transforming neighborhoods through intentional development and long-term investment.</div>
              </div>
              <div className="sp-pillar sp-pillar-psh">
                <div className="sp-p-num">03</div>
                <div className="sp-p-title">Sustainable Practices</div>
                <div className="sp-p-desc">Healthier, environmentally responsible construction for families and communities.</div>
              </div>
              <div className="sp-pillar sp-pillar-psh">
                <div className="sp-p-num">04</div>
                <div className="sp-p-title">Local Market Expertise</div>
                <div className="sp-p-desc">Deep roots in Houston and Birmingham markets, building where it matters most.</div>
              </div>
            </div>
            <div className="sp-stats">
              <div className="sp-stats-row">
                <div className="sp-stat sp-stat-psh">
                  <div className="sp-stat-num">2</div>
                  <div className="sp-stat-lbl">Major Markets Served</div>
                </div>
                <div className="sp-stat sp-stat-psh">
                  <div className="sp-stat-num">100<span style={{fontSize: '0.6em'}}>%</span></div>
                  <div className="sp-stat-lbl">Community-First Mission</div>
                </div>
                <div className="sp-stat sp-stat-psh">
                  <div className="sp-stat-num">↑</div>
                  <div className="sp-stat-lbl">Quality of Life &amp; Belonging</div>
                </div>
              </div>
            </div>
          </div>

          <div className="sp-panel-sep"></div>

          {/* R6 Panel */}
          <div className="sp-panel sp-panel-r6">
            <div className="sp-sec-label">What The R6™ Collective Brings</div>
            <div className="sp-panel-heading">The Right Path to<br/>Workforce Readiness</div>
            <div className="sp-panel-body">The R6™ Collective brings Fortune 1000-caliber workforce expertise directly to the communities Park Street Homes builds. Through The Right Path — our flagship workforce development program — we train, upskill, and reskill residents so they can access meaningful employment, advance their careers, and reinvest in the neighborhoods they call home.</div>
            <div className="sp-pillars">
              <div className="sp-pillar sp-pillar-r6">
                <div className="sp-p-num">01</div>
                <div className="sp-p-title">People</div>
                <div className="sp-p-desc">Precision hiring — talent acquisition, employer branding, and leadership assessment.</div>
              </div>
              <div className="sp-pillar sp-pillar-r6">
                <div className="sp-p-num">02</div>
                <div className="sp-p-title">Roles</div>
                <div className="sp-p-desc">Clear architecture — organizational design, job architecture, and talent infrastructure.</div>
              </div>
              <div className="sp-pillar sp-pillar-r6">
                <div className="sp-p-num">03</div>
                <div className="sp-p-title">Skills</div>
                <div className="sp-p-desc">Future ready — executive coaching, leadership development, and performance management.</div>
              </div>
              <div className="sp-pillar sp-pillar-r6">
                <div className="sp-p-num">04</div>
                <div className="sp-p-title">Environment</div>
                <div className="sp-p-desc">Culture performs — culture strategy, employee relations, and workforce engagement.</div>
              </div>
              <div className="sp-pillar sp-pillar-r6">
                <div className="sp-p-num">05</div>
                <div className="sp-p-title">Incentives</div>
                <div className="sp-p-desc">Rewards inspire — compensation, total rewards design, and recognition systems.</div>
              </div>
              <div className="sp-pillar sp-pillar-r6">
                <div className="sp-p-num">06</div>
                <div className="sp-p-title">Time</div>
                <div className="sp-p-desc">Anticipate needs — succession planning, talent analytics, and compliance management.</div>
              </div>
            </div>
            <div className="sp-stats">
              <div className="sp-stat sp-stat-r6 sp-stat-wide">
                <div className="sp-stat-text">Access to enterprise expertise and world-class training</div>
              </div>
              <div className="sp-stats-row">
                <div className="sp-stat sp-stat-r6">
                  <div className="sp-stat-num">100<span style={{fontSize: '0.55em'}}>+</span></div>
                  <div className="sp-stat-lbl">Years Combined Executive Experience</div>
                </div>
                <div className="sp-stat sp-stat-r6">
                  <div className="sp-stat-num">6</div>
                  <div className="sp-stat-lbl">Talent Dimensions Covered</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* VISION BAND */}
        <div className="sp-vision">
          <div className="sp-vb-psh">
            <div className="sp-vb-eye">Park Street Homes — Our Commitment</div>
            <div className="sp-vb-quote">"We build more than houses — we build thriving communities where people can <em>connect, belong, and grow.</em>"</div>
            <div className="sp-vb-url">parkstreethomes.com · Houston, TX &amp; Birmingham, AL</div>
          </div>
          <div className="sp-vb-r6">
            <div className="sp-vb-eye">The R6™ Collective — Our Promise</div>
            <div className="sp-vb-quote">"Talent issues are business issues — and <em>community issues.</em> We solve both."</div>
            <div className="sp-vb-url">connect@r6collective.com · www.r6collective.com</div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="sp-footer">
          <div className="sp-ft">Park Street Homes | <span className="hi">www.parkstreethomes.com</span></div>
          <div className="sp-ft-div"></div>
          <div className="sp-ft-center">RENEW · BUILD · ELEVATE</div>
          <div className="sp-ft-div"></div>
          <div className="sp-ft">The R6™ Collective | <span className="hi">www.r6collective.com</span></div>
        </div>
      </div>
    </div>
  );
};

export default StrategicPartnersPage;
